import { defineMessages } from 'react-intl-next';

const messages = defineMessages({
	// ── Header ──────────────────────────────────────────────────────────────
	heading: {
		id: 'malleable-ui-components.remix.heading',
		defaultMessage: 'Remix',
		description: 'Heading displayed at the top of the Remix overlay panel',
	},
	subtitle: {
		id: 'malleable-ui-components.remix.subtitle',
		defaultMessage: 'Visualize this content in a new way',
		description: 'Subtitle below the Create a remix heading',
	},
	labsLozenge: {
		id: 'malleable-ui-components.remix.labs-lozenge',
		defaultMessage: 'LABS',
		description: 'Label for the LABS lozenge in the Remix overlay header',
	},
	labsLozengeTooltip: {
		id: 'malleable-ui-components.remix.option.lozenge-tooltip',
		defaultMessage: 'Beta: This feature is still in development.',
		description: 'Tooltip for the LABS lozenge tag',
	},
	remixIconAlt: {
		id: 'malleable-ui-components.remix.icon-alt',
		defaultMessage: 'Remix icon',
		description: 'Alt text for the decorative remix gradient icon in the overlay header',
	},

	// ── Describe input ─────────────────────────────────────────────────────
	describePlaceholder: {
		id: 'malleable-ui-components.remix.describe-placeholder',
		defaultMessage: 'Or describe your remix...',
		description: 'Placeholder for the remix description input field',
	},
	describeSubmitLabel: {
		id: 'malleable-ui-components.remix.describe-submit-label',
		defaultMessage: 'Submit',
		description: 'Accessible label for the describe input submit button',
	},

	// ── Top-level option labels ────────────────────────────────────────────
	remixOptionChart: {
		id: 'malleable-ui-components.remix.option.chart',
		defaultMessage: 'Chart',
		description: 'Label for the Chart remix option',
	},
	remixOptionDiagram: {
		id: 'malleable-ui-components.remix.option.diagram',
		defaultMessage: 'Diagram',
		description: 'Label for the Diagram remix option',
	},
	remixOptionInfographic: {
		id: 'malleable-ui-components.remix.option.infographic',
		defaultMessage: 'Infographic',
		description: 'Label for the Infographic remix option',
	},
	remixOptionVisualization: {
		id: 'malleable-ui-components.remix.option.visualization',
		defaultMessage: 'Visualization',
		description: 'Label for the Visualization remix option',
	},

	// ── Top-level option icon alts ─────────────────────────────────────────
	chartIconAlt: {
		id: 'malleable-ui-components.remix.option.chart-icon-alt',
		defaultMessage: 'Chart icon',
		description: 'Alt text for the chart option icon',
	},
	diagramIconAlt: {
		id: 'malleable-ui-components.remix.option.diagram-icon-alt',
		defaultMessage: 'Diagram icon',
		description: 'Alt text for the diagram option icon',
	},
	infographicIconAlt: {
		id: 'malleable-ui-components.remix.option.infographic-icon-alt',
		defaultMessage: 'Infographic icon',
		description: 'Alt text for the infographic option icon',
	},
	visualizationIconAlt: {
		id: 'malleable-ui-components.remix.option.visualization-icon-alt',
		defaultMessage: 'Visualization icon',
		description: 'Alt text for the visualization option icon',
	},

	// ── Top-level option tooltips ──────────────────────────────────────────
	chartTooltip: {
		id: 'malleable-ui-components.remix.option.chart-tooltip',
		defaultMessage: 'View your data as a chart or graph',
		description: 'Tooltip for the Chart remix option',
	},
	diagramTooltip: {
		id: 'malleable-ui-components.remix.option.diagram-tooltip',
		defaultMessage: 'Show relationships with a diagram',
		description: 'Tooltip for the Diagram remix option',
	},
	infographicTooltip: {
		id: 'malleable-ui-components.remix.option.infographic-tooltip',
		defaultMessage: 'Summarize key points with an infographic',
		description: 'Tooltip for the Infographic remix option',
	},
	visualizationTooltip: {
		id: 'malleable-ui-components.remix.option.visualization-tooltip',
		defaultMessage: 'Bring your content to life with a bespoke visual',
		description: 'Tooltip for the Visualization remix option',
	},

	// ── Sub-type labels (charts) ───────────────────────────────────────────
	subTypeLineChart: {
		id: 'malleable-ui-components.remix.sub-type.line-chart',
		defaultMessage: 'Line chart',
		description: 'Sub-type label for a line chart within the Chart remix option',
	},
	subTypeAreaChart: {
		id: 'malleable-ui-components.remix.sub-type.area-chart',
		defaultMessage: 'Area chart',
		description: 'Sub-type label for an area chart within the Chart remix option',
	},
	subTypeBarChart: {
		id: 'malleable-ui-components.remix.sub-type.bar-chart',
		defaultMessage: 'Bar chart',
		description: 'Sub-type label for a bar chart within the Chart remix option',
	},
	subTypeFunnelChart: {
		id: 'malleable-ui-components.remix.sub-type.funnel-chart',
		defaultMessage: 'Funnel chart',
		description: 'Sub-type label for a funnel chart within the Chart remix option',
	},
	subTypePieChart: {
		id: 'malleable-ui-components.remix.sub-type.pie-chart',
		defaultMessage: 'Pie chart',
		description: 'Sub-type label for a pie chart within the Chart remix option',
	},
	subTypeGanttChart: {
		id: 'malleable-ui-components.remix.sub-type.gantt-chart',
		defaultMessage: 'Gantt chart',
		description: 'Sub-type label for a Gantt chart within the Chart remix option',
	},

	// ── Sub-type labels (diagrams) ─────────────────────────────────────────
	subTypeMindMap: {
		id: 'malleable-ui-components.remix.sub-type.mind-map',
		defaultMessage: 'Mindmap',
		description: 'Sub-type label for a mind map within the Diagram remix option',
	},
	subTypeFlowChart: {
		id: 'malleable-ui-components.remix.sub-type.flow-chart',
		defaultMessage: 'Flowchart',
		description: 'Sub-type label for a flowchart within the Diagram remix option',
	},

	// ── Sub-type labels (infographics) ─────────────────────────────────────
	subTypeClassicStyle: {
		id: 'malleable-ui-components.remix.sub-type.classic-style',
		defaultMessage: 'Classic style',
		description: 'Sub-type label for classic style within the Infographic remix option',
	},
	subTypeSketchStyle: {
		id: 'malleable-ui-components.remix.sub-type.sketch-style',
		defaultMessage: 'Sketch style',
		description: 'Sub-type label for sketch style within the Infographic remix option',
	},

	// ── Sub-type labels (visual aids) ──────────────────────────────────────
	subTypeFlipCard: {
		id: 'malleable-ui-components.remix.sub-type.flip-card',
		defaultMessage: 'Flip card',
		description: 'Sub-type label for a flip card within the Visualization remix option',
	},
	subTypeQuadrant: {
		id: 'malleable-ui-components.remix.sub-type.quadrant',
		defaultMessage: 'Quadrant',
		description: 'Sub-type label for a quadrant within the Visualization remix option',
	},
	subTypeTreeMap: {
		id: 'malleable-ui-components.remix.sub-type.tree-map',
		defaultMessage: 'Tree map',
		description: 'Sub-type label for a tree map within the Visualization remix option',
	},
	subTypeTimeline: {
		id: 'malleable-ui-components.remix.sub-type.timeline',
		defaultMessage: 'Timeline',
		description: 'Sub-type label for a timeline within the Visualization remix option',
	},
	subTypeGeographicalMap: {
		id: 'malleable-ui-components.remix.sub-type.geographical-map',
		defaultMessage: 'Geographical map',
		description: 'Sub-type label for a geographical map within the Visualization remix option',
	},
	subTypeOrgChart: {
		id: 'malleable-ui-components.remix.sub-type.org-chart',
		defaultMessage: 'Org chart',
		description: 'Sub-type label for an org chart within the Visualization remix option',
	},

	// ── Pick for me ────────────────────────────────────────────────────────
	pickForMe: {
		id: 'malleable-ui-components.remix.pick-for-me',
		defaultMessage: 'Pick for me',
		description:
			'Button label that lets Rovo automatically choose the best sub-type for the selected remix option',
	},

	// ── Unused legacy keys kept for i18n backwards compat ──────────────────
	selectedContent: {
		id: 'malleable-ui-components.remix.selected-content',
		defaultMessage: 'Selected content',
		description: 'Label shown above the displayed selected text content in the Remix overlay',
	},
	noContent: {
		id: 'malleable-ui-components.remix.no-content',
		defaultMessage: 'No content selected',
		description: 'Fallback message displayed when no text content is currently selected',
	},
});

export default messages;
